import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback();
    
    this.refs.searchPageInput.addEventListener('focus', this.handleFocus);
    this.refs.searchPageInput.addEventListener('blur', this.handleBlur);
    this.refs.searchPageInput.addEventListener('input', this.handleInputUpdate);
    window.addEventListener('resize', this.handleResize);
    
    // Initial check in case it's auto-focused
    if (document.activeElement === this.refs.searchPageInput) {
      this.handleFocus();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.searchPageInput.removeEventListener('focus', this.handleFocus);
    this.refs.searchPageInput.removeEventListener('blur', this.handleBlur);
    this.refs.searchPageInput.removeEventListener('input', this.handleInputUpdate);
    window.removeEventListener('resize', this.handleResize);
    document.body.classList.remove('search-page-input-focused');
  }

  handleFocus = () => {
    document.body.classList.add('search-page-input-focused');
    this.repositionCloudSearch();
  };

  handleBlur = () => {
    setTimeout(() => {
      document.body.classList.remove('search-page-input-focused');
    }, 200);
  };

  handleInputUpdate = () => {
    this.repositionCloudSearch();
  };

  handleResize = () => {
    if (document.activeElement === this.refs.searchPageInput) {
      this.repositionCloudSearch();
    }
  };

  repositionCloudSearch = () => {
    requestAnimationFrame(() => {
      const inputRect = this.refs.searchPageInput.getBoundingClientRect();
      let styleEl = document.getElementById('cloud-search-reposition-style');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'cloud-search-reposition-style';
        document.head.appendChild(styleEl);
      }
      
      const top = inputRect.bottom + window.scrollY;
      const left = inputRect.left + window.scrollX;
      const width = inputRect.width;
      
      styleEl.innerHTML = `
        body.search-page-input-focused .cloud-search-results,
        body.search-page-input-focused .cs-dropdown {
          position: absolute !important;
          top: ${top}px !important;
          left: ${left}px !important;
          width: ${width}px !important;
          z-index: 999999 !important;
        }
      `;
    });
  };

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopImmediatePropagation();
      const value = this.refs.searchPageInput.value.trim();
      const searchUrl = new URL('/search', location.origin);
      searchUrl.searchParams.set('q', value);
      window.location.assign(searchUrl.toString());
      return;
    }
    
    this.#debouncedHandleKeyDown(event);
  };

  #debouncedHandleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
